import { TitleCasePipe } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, signal, Signal, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadge } from '@angular/material/badge';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatOption } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { NotificationsDialogComponent } from '@app/core/notifications/notifications-dialog/notifications-dialog.component';
import { NotificationsStateService } from '@app/core/notifications/notifications-state.service';
import { environment } from '@environment/environment';
import { MaterialSymbol } from 'material-symbols';
import { GlobalInlineBannerStateService } from '../../components/global-inline-banner/global-inline-banner-state.service';
import { GlobalInlineBannerComponent } from '../../components/global-inline-banner/global-inline-banner.component';
import { MarginDirective } from '../../directives/spacing/margin.directive';
import { ApplicationNames } from '../../enums/applications.types';
import { AppHelpers } from '../../helpers/app-helpers';
import { DialogHelpers } from '../../helpers/dialog-helpers';
import { AssetTreeChildSimple } from '../../models/components-asset-tree';
import { BadgeNumberPipe } from '../../pipes/badge-number.pipe';
import { NoZeroFormatterPipe } from '../../pipes/no-zero-formatter.pipe';
import { ApplicationAuthService } from '../../services/application-auth.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { NavigateToService } from '../../services/helper-services/navigate-to.service';
import { AppStateService } from '../app-state.service';
import { ExcludedUrlsPipe } from '../sidebar/excluded-urls.pipe';

@Component({
  selector: 'app-header',
  imports: [
    MatFormField,
    MatLabel,
    MatOption,
    MatButtonModule,
    MatIconButton,
    MatIcon,
    MatMenu,
    MatMenuTrigger,
    MatDivider,
    MatMenuItem,
    RouterLink,
    ExcludedUrlsPipe,
    MatSuffix,
    TitleCasePipe,
    MatTooltip,
    MatBadge,
    BadgeNumberPipe,
    NoZeroFormatterPipe,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    GlobalInlineBannerComponent,
    MarginDirective
  ],
  templateUrl: './header.component.html'
})
export class HeaderComponent {
  @ViewChild('notificationsButton', { read: ElementRef }) button: ElementRef<HTMLButtonElement>;

  readonly contactSupportIcon: MaterialSymbol = 'contact_support';
  readonly rocketLaunchIcon: MaterialSymbol = 'rocket_launch';
  readonly accountCircleIcon: MaterialSymbol = 'account_circle';
  readonly settingsAccountBoxIcon: MaterialSymbol = 'settings_account_box';
  readonly homeIcon: MaterialSymbol = 'home';
  readonly logOutIcon: MaterialSymbol = 'logout';
  readonly closeIcon: MaterialSymbol = 'close';
  readonly notificationsIcon: MaterialSymbol = 'notifications';
  readonly notificationsOffIcon: MaterialSymbol = 'notifications_off';

  private readonly appStateService = inject(AppStateService);
  private readonly notificationStateService = inject(NotificationsStateService);
  private readonly authService = inject(ApplicationAuthService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly globalBannerStateService = inject(GlobalInlineBannerStateService);

  protected readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly navigateTo = inject(NavigateToService);

  readonly $user = this.appStateService.user;
  readonly $breadcrumbs = this.appStateService.breadcrumbs;
  readonly $installations = this.appStateService.availableInstallations;
  readonly $selectedInstallation = this.appStateService.installation;
  readonly $selectedInstallationName = computed(() => this.$selectedInstallation()?.name ?? '');

  readonly $application = this.appStateService.application;
  readonly $entity = this.appStateService.entity;
  readonly $isUserAdmin = this.appStateService.isAdmin;
  readonly $isOutsideElement = this.appStateService.isOutsideElement;
  readonly $includeInApp = this.notificationStateService
    ? this.notificationStateService.assignmentNotifications
    : signal(false);
  readonly $notifications = this.notificationStateService
    ? this.notificationStateService.unreadNotifications
    : signal([]);
  readonly $totalUnread = this.notificationStateService
    ? this.notificationStateService.totalUnread
    : signal(0);
  readonly $element = this.appStateService.element;

  readonly $enableNotifications = this.featureFlagService.enableNotifications;
  readonly $filteredInstallations: Signal<AssetTreeChildSimple[]>;

  readonly $globalBanner = this.globalBannerStateService.banner;

  readonly helpDeskLink = AppHelpers.HELPDESK_URL;
  readonly version = environment.version;
  readonly ApplicationNames = ApplicationNames;

  readonly searchControl = new FormControl<string>(this.$selectedInstallationName());

  protected readonly $userHasElementsAccess = this.appStateService.hasAnyElementAccess;

  constructor() {
    effect(() => {
      this.searchControl.setValue(this.$selectedInstallationName(), { emitEvent: false });
    });

    this.$filteredInstallations = computed(() => {
      const value = this.searchControl.value;
      return this._filter(value ?? '');
    });
  }

  displayInstallation = (value: AssetTreeChildSimple | string | null): string => {
    if (!value) {
      return '';
    }
    return typeof value === 'string' ? value : value.name;
  };

  private _filter(value: AssetTreeChildSimple | string): AssetTreeChildSimple[] {
    const filterValue = (typeof value === 'string' ? value : (value?.name ?? '')).toLowerCase();
    return this.$installations().filter(option => option.name.toLowerCase().includes(filterValue));
  }

  onFocus(): void {
    this.searchControl.setValue('');
  }

  onBlur(): void {
    this.searchControl.setValue(this.$selectedInstallationName(), { emitEvent: false });
  }

  changeInstallation(installation: AssetTreeChildSimple | null): void {
    this.appStateService.setInstallation(installation);

    if (!installation) {
      this.searchControl.reset();
    }
  }

  openNotificationsDialog(): void {
    const button = this.button.nativeElement.getBoundingClientRect();
    const dialogConfig: MatDialogConfig = {
      width: '480px',
      minWidth: '480px',
      maxWidth: '80vw',
      panelClass: DialogHelpers.HIDDEN_DIALOG,
      disableClose: false
    };

    const dialogRef = this.dialog.open(NotificationsDialogComponent, dialogConfig);
    DialogHelpers.moveDialog(dialogRef, button.right, button.bottom);
  }

  openNotifications(): void {
    this.navigateTo.notifications();
  }

  signOut() {
    this.authService.logout();
  }

  navigateToAdmin(): void {
    window.location.href = environment.sourceApp;
  }

  navigateHome(): void {
    window.location.href = environment.home;
  }
}
